/**
 * NMS Mobile Sidebar Component
 *
 * Modern mobile navigation sidebar with 2-level panel support.
 * Follows NMS component instance architecture with performance-first approach.
 *
 * Features:
 * - Smooth panel transitions
 * - Keyboard navigation support
 * - Touch gesture support
 * - Accessibility compliant
 * - Component instance pattern
 */
class NMSMobileSidebar extends window.NMSBaseClass {
  #isOpen = false;
  #currentPanel = 'main';
  #panelHistory = ['main'];
  #animationDuration = 300;
  #touchStartX = 0;
  #touchCurrentX = 0;
  #isDragging = false;
  #handleFocusTrap = null;

  constructor(element) {
    super();
    this.element = element;
    this.sidebarId = element.dataset.sidebarId || 'default';

    // Store in global registry
    window.neemans.mobileSidebar = window.neemans.mobileSidebar || {};
    window.neemans.mobileSidebar[this.sidebarId] = this;
  }

  init() {
    super.init();
    this.#setupKeyboardNavigation();
    this.#setupTouchGestures();
    this.#setupBodyScrollLock();
  }

  #setupKeyboardNavigation() {
    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#isOpen) {
        this.close();
      }
    });

    // Enter/Space for expandable links
    const expandableLinks = this.element.querySelectorAll('[data-action="open-panel"]');

    for (const link of expandableLinks) {
      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          link.click();
        }
      });
    }

    // Enter/Space for back buttons
    const backButtons = this.element.querySelectorAll('[data-action="back"]');

    for (const btn of backButtons) {
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click();
        }
      });
    }
  }

  #setupTouchGestures() {
    if (!this.refs.content) return;

    this.refs.content.addEventListener('touchstart', (e) => {
      this.#touchStartX = e.touches[0].clientX;
      this.#isDragging = false;
    }, { passive: true });

    this.refs.content.addEventListener('touchmove', (e) => {
      if (!this.#isDragging) return;

      this.#touchCurrentX = e.touches[0].clientX;
      const diffX = this.#touchCurrentX - this.#touchStartX;

      // Only allow left swipe (negative direction)
      if (diffX < 0) {
        const translateX = Math.min(0, diffX);
        this.refs.content.style.transform = `translateX(${translateX}px)`;
      }
    }, { passive: true });

    this.refs.content.addEventListener('touchend', () => {
      if (!this.#isDragging) return;

      const diffX = this.#touchCurrentX - this.#touchStartX;
      const threshold = this.refs.content.offsetWidth * 0.3;

      if (Math.abs(diffX) > threshold && diffX < 0) {
        this.close();
      } else {
        this.refs.content.style.transform = 'translateX(0)';
      }

      this.#isDragging = false;
    }, { passive: true });
  }

  #setupBodyScrollLock() {
    // Body scroll lock is now handled directly in open()/close() methods
    // Sets document.body.style.overflow to prevent background scrolling
  }

  // Event handlers - auto-bound via data-nms-click
  handleClose() {
    this.close();
  }

  handleOpenPanel(event, panelId) {
    event.preventDefault();
    this.openPanel(panelId);
  }

  handleBack() {
    this.goBack();
  }

  // Public API methods
  open() {
    if (this.#isOpen) return;

    this.#isOpen = true;
    this.element.setAttribute('data-state', 'open');

    // Show sidebar
    this.element.style.display = 'block';

    // Trigger reflow
    this.element.offsetHeight;

    // Add open class for CSS animations
    this.element.classList.add('nms-mobile-sidebar--open');

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Focus management
    this.#trapFocus();

    // Dispatch event
    this.dispatchNMSEvent('sidebar-open', { sidebar: this });

    // Auto-focus close button for accessibility
    setTimeout(() => {
      if (this.refs.closeBtn) {
        this.refs.closeBtn.focus();
      }
    }, this.#animationDuration);
  }

  close() {
    if (!this.#isOpen) return;

    this.#isOpen = false;
    this.element.setAttribute('data-state', 'closed');

    // Remove open class
    this.element.classList.remove('nms-mobile-sidebar--open');

    // Unlock body scroll
    document.body.style.overflow = '';

    // Reset to main panel
    this.#resetToMainPanel();

    // Release focus trap
    this.#releaseFocus();

    // Dispatch event
    this.dispatchNMSEvent('sidebar-close', { sidebar: this });

    // Hide after animation
    setTimeout(() => {
      this.element.style.display = 'none';
      if (this.refs.content) {
        this.refs.content.style.transform = '';
      }
    }, this.#animationDuration);
  }

  openPanel(panelId) {
    const targetPanel = this.element.querySelector(`[data-panel="${panelId}"]`);
    if (!targetPanel) return;

    const currentPanelEl = this.element.querySelector(`[data-panel="${this.#currentPanel}"]`);

    // Update history
    this.#panelHistory.push(panelId);

    // Hide current panel
    if (currentPanelEl) {
      currentPanelEl.style.transform = 'translateX(-100%)';
      currentPanelEl.setAttribute('aria-hidden', 'true');
    }

    // Show target panel with proper animation
    targetPanel.hidden = false;
    targetPanel.setAttribute('aria-hidden', 'false');

    // Force reflow to ensure the panel starts from the right position
    targetPanel.offsetHeight;

    // Animate to the visible position
    setTimeout(() => {
      targetPanel.style.transform = 'translateX(0)';
    }, 10);

    // Update current panel
    this.#currentPanel = panelId;

    // Update aria-expanded on trigger button
    const triggerBtn = this.element.querySelector(`[data-panel-id="${panelId}"]`);
    if (triggerBtn) {
      triggerBtn.setAttribute('aria-expanded', 'true');
    }

    // Focus first interactive element in new panel
    setTimeout(() => {
      const firstFocusable = targetPanel.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 100);

    // Dispatch event
    this.dispatchNMSEvent('sidebar-panel-change', {
      from: currentPanelEl?.dataset.panel,
      to: panelId,
      sidebar: this
    });
  }

  goBack() {
    if (this.#panelHistory.length <= 1) return;

    // Remove current panel from history
    this.#panelHistory.pop();

    // Get previous panel
    const previousPanelId = this.#panelHistory[this.#panelHistory.length - 1];
    const previousPanel = this.element.querySelector(`[data-panel="${previousPanelId}"]`);
    const currentPanelEl = this.element.querySelector(`[data-panel="${this.#currentPanel}"]`);

    if (!previousPanel) return;

    // Hide current panel
    if (currentPanelEl) {
      currentPanelEl.style.transform = 'translateX(100%)';
      currentPanelEl.setAttribute('aria-hidden', 'true');

      // Update aria-expanded on trigger button
      const triggerBtn = this.element.querySelector(`[data-panel-id="${this.#currentPanel}"]`);
      if (triggerBtn) {
        triggerBtn.setAttribute('aria-expanded', 'false');
      }

      setTimeout(() => {
        currentPanelEl.hidden = true;
        currentPanelEl.style.transform = '';
      }, this.#animationDuration);
    }

    // Show previous panel
    previousPanel.hidden = false;
    previousPanel.style.transform = 'translateX(0)';
    previousPanel.setAttribute('aria-hidden', 'false');

    // Update current panel
    this.#currentPanel = previousPanelId;

    // Focus management
    setTimeout(() => {
      if (previousPanelId === 'main') {
        const firstFocusable = previousPanel.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
          firstFocusable.focus();
        }
      } else {
        const backBtn = previousPanel.querySelector('[data-action="back"]');
        if (backBtn) {
          backBtn.focus();
        }
      }
    }, 100);
  }

  toggle() {
    if (this.#isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  #resetToMainPanel() {
    const panels = this.element.querySelectorAll('[data-panel]');

    // Hide all panels except main
    for (const panel of panels) {
      if (panel.dataset.panel !== 'main') {
        panel.hidden = true;
        // Reset sub-panels to their off-screen position
        panel.style.transform = 'translateX(100%)';
        panel.setAttribute('aria-hidden', 'true');
      }
    }

    // Show main panel
    const mainPanel = this.element.querySelector('[data-panel="main"]');
    if (mainPanel) {
      mainPanel.hidden = false;
      mainPanel.style.transform = 'translateX(0)';
      mainPanel.setAttribute('aria-hidden', 'false');
    }

    // Reset aria-expanded on all trigger buttons
    const expandableLinks = this.element.querySelectorAll('[data-action="open-panel"]');

    for (const link of expandableLinks) {
      link.setAttribute('aria-expanded', 'false');
    }

    // Reset state
    this.#currentPanel = 'main';
    this.#panelHistory = ['main'];
  }

  #trapFocus() {
    const focusableElements = this.element.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    this.element.addEventListener('keydown', this.#handleFocusTrap = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    });
  }

  #releaseFocus() {
    if (this.#handleFocusTrap) {
      this.element.removeEventListener('keydown', this.#handleFocusTrap);
      this.#handleFocusTrap = null;
    }
  }

  // Getters for external access
  get isOpen() {
    return this.#isOpen;
  }

  get currentPanel() {
    return this.#currentPanel;
  }

  get panelHistory() {
    return [...this.#panelHistory];
  }

  destroy() {
    super.destroy();

    // Clean up global reference
    if (window.neemans.mobileSidebar?.[this.sidebarId]) {
      delete window.neemans.mobileSidebar[this.sidebarId];
    }

    // Ensure body scroll is unlocked
    document.body.style.overflow = '';

    this.#releaseFocus();
  }
}

// Initialize components
function initNMSMobileSidebar() {
  if (!window.NMSBaseClass) {
    console.error('NMSBaseClass required for NMSMobileSidebar');
    return;
  }

  const elements = document.querySelectorAll('[data-nms-mobile-sidebar]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSMobileSidebar(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }

  // Initialize trigger buttons
  initMobileSidebarTriggers();
}

// Initialize trigger buttons
function initMobileSidebarTriggers() {
  const triggers = document.querySelectorAll('[data-nms-mobile-trigger]');

  for (const trigger of triggers) {
    if (!trigger.hasAttribute('data-trigger-initialized')) {
      const sidebarId = trigger.dataset.sidebarId || 'mobile-menu';

      trigger.addEventListener('click', () => {
        const sidebar = window.neemans.mobileSidebar?.[sidebarId];
        if (sidebar) {
          sidebar.toggle();
          trigger.setAttribute('aria-expanded', sidebar.isOpen ? 'true' : 'false');
        }
      });

      trigger.setAttribute('data-trigger-initialized', 'true');
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSMobileSidebar);
} else {
  initNMSMobileSidebar();
}

// Register custom element
if (!window.customElements.get('nms-mobile-sidebar')) {
  window.customElements.define('nms-mobile-sidebar', class extends HTMLElement {
    connectedCallback() {
      initNMSMobileSidebar();
    }
  });
}
